import {useEffect, useRef, useState} from 'react';
import {useNavigate, useParams as useRouteParams} from 'react-router-dom';
import {Button, Tag} from 'letrus-ui';
import EmptyWarning from 'components/EmptyWarning';
import {fetchTrailCompletedService} from './trailCompleted.service';
import {TrailCompletedData, EssayResultOrPending} from './types';
import styles from './TrailCompleted.module.scss';

// Colors reused from the design system (same as FeedbackCommentCards): green/yellow/red.
const SUCCESS = {main: '#2fac66', bg: '#c6f1d3'};
const WARNING = {main: '#e6ac00', bg: '#ffeac4'};
const DANGER = {main: '#d64545', bg: '#fbd5d5'};
const NEUTRAL = {main: '#8c8c8c', bg: '#ececec'};

const ESSAY_TAG: Record<
  Exclude<EssayResultOrPending, null>,
  {text: string; main: string; bg: string}
> = {
  certo: {text: 'Certo', ...SUCCESS},
  meio: {text: 'Meio certo', ...WARNING},
  revisar: {text: 'Precisa revisar', ...DANGER},
};

// `null` (essay not yet evaluated) falls back to the neutral state instead of breaking the lookup.
function getEssayTag(value: EssayResultOrPending) {
  return value == null
    ? {text: 'Aguardando correção', ...NEUTRAL}
    : ESSAY_TAG[value];
}

function TrailCompleted(): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [data, setData] = useState<TrailCompletedData>();

  const {learning_path_id} = useRouteParams();
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Reset on every run: the cleanup sets this false, so without resetting a fetch triggered by
    // a learning_path_id change would have its result discarded by the previous run's cleanup.
    isMountedRef.current = true;

    fetchTrailCompletedService(learning_path_id)
      .then(({data: trailData}) => {
        if (!isMountedRef.current) return;
        setData(trailData);
        setIsLoading(false);
      })
      .catch(() => {
        // Real failure: sets an error to render EmptyWarning instead of an infinite skeleton
        // (same regression already fixed in PracticeTestFeedback).
        if (isMountedRef.current) {
          setHasError(true);
          setIsLoading(false);
        }
      });

    return () => {
      isMountedRef.current = false;
    };
  }, [learning_path_id]);

  if (hasError) {
    return (
      <main className={styles.container}>
        <EmptyWarning subtitle="Não foi possível carregar o resultado da trilha." />
      </main>
    );
  }

  if (isLoading || !data) {
    return <p className={styles.loading}>Carregando…</p>;
  }

  return (
    <main className={styles.container} data-focusid="container">
      <div className={styles.content}>
        {/* Activity header */}
        <section className={styles.card}>
          <p className={styles.activityLabel}>{data.activityLabel}</p>
          <h1 className={styles.title}>{data.title}</h1>

          <div className={styles.completedBadge}>
            <span className={styles.completedCheck} aria-hidden="true">
              ✓
            </span>
            <div>
              <p className={styles.completedLine}>Questionário</p>
              <p className={styles.completedSub}>Concluído</p>
            </div>
          </div>
        </section>

        {/* Result card */}
        <section className={`${styles.card} ${styles.resultCard}`}>
          <p className={styles.resultLabel}>{data.resultLabel}</p>
          <h2 className={styles.outcome}>
            {data.outcome.label}{' '}
            <span className={styles.outcomeEmoji} aria-hidden="true">
              {data.outcome.emoji}
            </span>
          </h2>
          <p className={styles.reinforcement}>{data.reinforcement}</p>
        </section>

        {/* Performance per question */}
        <section className={styles.card}>
          <h3 className={styles.heading}>Como você foi em cada questão</h3>

          <ul className={styles.list}>
            {data.objectives.map((ok, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`obj-${i}`} className={styles.item}>
                <span className={styles.itemLabel}>Questão {i + 1}</span>
                <Tag
                  text={ok ? 'Certo' : 'Errado'}
                  variant="readOnly"
                  mainColor={ok ? SUCCESS.main : DANGER.main}
                  backgroundColor={ok ? SUCCESS.bg : DANGER.bg}
                />
              </li>
            ))}

            {data.essays.map((value, i) => {
              const tag = getEssayTag(value);

              return (
                // eslint-disable-next-line react/no-array-index-key
                <li key={`essay-${i}`} className={styles.item}>
                  <span className={styles.itemLabel}>
                    Questão {data.objectives.length + i + 1}{' '}
                    <span className={styles.itemHint}>(escrita)</span>
                  </span>
                  <Tag
                    text={tag.text}
                    variant="readOnly"
                    mainColor={tag.main}
                    backgroundColor={tag.bg}
                  />
                </li>
              );
            })}
          </ul>
        </section>

        {/* Closing */}
        <section className={`${styles.card} ${styles.closingCard}`}>
          <p className={styles.closingPhrase}>{data.closingPhrase}</p>
          <div className={styles.closingActions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              Voltar
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => navigate('/')}
            >
              Continuar
            </Button>
          </div>
        </section>
      </div>
    </main>
  );
}

export default TrailCompleted;
